import { Link } from 'react-router-dom';
import type { PtoRdImportSummary } from '@shared/api';
import { MoreList } from './NoteList';
import { Code, Details, ErrorText, List, Muted } from './styles';

type Summary = Partial<PtoRdImportSummary>;

/** В итоге применения есть что показать. */
export function hasSummary(s: Summary | null | undefined): boolean {
  return Boolean(
    s &&
    (s.revisions?.length || s.errors?.length || s.skipped?.length || s.projects_created?.length),
  );
}

/**
 * Итог применения раскладки: сначала ошибки (что не загрузилось и почему),
 * затем загруженные ревизии (с перенесёнными листами прежних ревизий), новые
 * проекты и пропуски.
 */
export function ApplyResult({ summary }: { summary: Summary }) {
  const revisions = summary.revisions ?? [];
  const errors = summary.errors ?? [];
  const skipped = summary.skipped ?? [];
  const created = summary.projects_created ?? [];
  const carried = revisions.reduce((s, r) => s + (r.carried ?? 0), 0);
  const newDocs = revisions.filter((r) => r.document_created).length;
  return (
    <>
      {errors.length > 0 ? (
        <>
          <ErrorText>Не загружено из-за ошибок — {errors.length}:</ErrorText>
          <MoreList
            items={errors}
            itemKey={(e, i) => `${i}:${e.code ?? e.titul ?? ''}`}
            render={(e) => (
              <>
                {e.code ? <Code>{e.code}</Code> : `титул ${e.titul ?? '—'}`}: {e.message}
              </>
            )}
          />
        </>
      ) : null}
      <Muted>
        Загружено ревизий: {revisions.length} (новых документов — {newDocs}); новых проектов —{' '}
        {created.length}; пропущено — {skipped.length}
        {carried > 0 ? `; перенесено листов прежних ревизий — ${carried}` : ''}.
      </Muted>
      {created.length > 0 ? (
        <List>
          {created.map((p) => (
            <li key={p.id}>
              новый проект: <Link to={`/agents/pto/${p.id}`}>{p.name}</Link>
            </li>
          ))}
        </List>
      ) : null}
      {revisions.length > 0 ? (
        <Details>
          <summary>Загруженные ревизии ({revisions.length})</summary>
          <MoreList
            items={revisions}
            limit={50}
            itemKey={(r) => r.revision_id}
            render={(r) => (
              <>
                <Link to={`/agents/pto/${r.project_id}`}>
                  <Code>{r.code}</Code>
                </Link>
                {r.rev_label ? ` — ревизия ${r.rev_label}` : ''}
                {r.document_created ? ' · новый документ' : ''}
                {r.carried ? ` · перенесено листов прежней ревизии: ${r.carried}` : ''}
              </>
            )}
          />
        </Details>
      ) : null}
      {skipped.length > 0 ? (
        <Details>
          <summary>Пропущено ({skipped.length})</summary>
          <MoreList
            items={skipped}
            limit={50}
            itemKey={(s, i) => `${i}:${s.code}`}
            render={(s) => (
              <>
                <Code>{s.code}</Code> — {s.reason}
              </>
            )}
          />
        </Details>
      ) : null}
    </>
  );
}
